/**
 * TanStack Query hooks for session templates (the web Session Builder): the list, its mutations and
 * block editing.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../client'
import type { LocationEquipmentOut } from '../locations'
import type {
  SessionTemplate,
  SessionTemplateIn,
  SessionTemplatePatch,
  TemplateMuscleSummaryOut,
} from '../types'
import { useCrudMutations } from './shared'

// ── Session templates (web Session Builder) ──────────────────────────

const TEMPLATES_KEY = ['templates'] as const

/**
 * All saved session templates, ordered by position.
 * Query key: ['templates']
 */
export function useSessionTemplates() {
  return useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () => apiFetch<SessionTemplate[]>('/api/templates'),
  })
}

function useTemplateMutations() {
  return useCrudMutations<SessionTemplate, SessionTemplateIn, SessionTemplateIn>(
    '/api/templates',
    TEMPLATES_KEY,
  )
}

/** Create a new session template. */
export function useCreateTemplate() {
  return useTemplateMutations().create
}

/** Replace an existing session template (name, position, blocks). */
export function useUpdateTemplate() {
  return useTemplateMutations().update
}

/** Delete a session template. */
export function useDeleteTemplate() {
  return useTemplateMutations().remove
}

/**
 * Rename, move to another gym, or reposition one session (SB-5).
 *
 * `PATCH`, not `PUT`: the full-replace `PUT` carries `blocks`, so renaming
 * through it would mean shipping the whole session back to say one word.
 *
 * **An absent key means "leave it"; an explicit `null` means "clear it".** The
 * server reads `exclude_unset`, so a rename must OMIT `location_id` rather
 * than send it as null -- sending null would move the session out of its gym.
 *
 * Not folded into `useCrudMutations`: that helper's update leg is keyed to one
 * `updateMethod` for the whole triad, and session templates need `PUT` for the
 * block replace and `PATCH` for this. The invalidation is written out here for
 * the same reason the rest of the file writes it out -- the global `staleTime`
 * is `Infinity`, so nothing refetches on its own, and there is no broad
 * invalidation to lean on any more (the sync watcher that did it for a finished
 * sync pipeline went with the pipeline, #387).
 */
export function usePatchTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SessionTemplatePatch }) =>
      apiFetch<SessionTemplate>(`/api/templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...TEMPLATES_KEY] }),
  })
}

/**
 * Copy a session, blocks and gym included (SB-5).
 *
 * No body: the server names the copy and deep-copies the blocks, so there is
 * nothing for a client to assemble and nothing it can get wrong. The list is
 * invalidated and NOT navigated away from -- making "Push Day B" from "Push
 * Day A" is the point, and landing in the editor would interrupt it.
 */
export function useDuplicateTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<SessionTemplate>(`/api/templates/${id}/duplicate`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...TEMPLATES_KEY] }),
  })
}

// ── SB-4: block editing ─────────────────────────────────────────────────────

/** Query key for one template's muscle summary. Exported so the Builder can
 *  invalidate it on save -- the global `staleTime` is `Infinity`, so nothing
 *  refetches on its own and a summary would otherwise describe the template as
 *  it was before the rows were edited. */
export const muscleSummaryKey = (templateId: number) =>
  ['template-muscle-summary', templateId] as const

/**
 * Which muscles each block of a template actually trains (SB-4).
 *
 * `blocks` is POSITIONALLY ALIGNED with the template's own `blocks` -- one
 * entry per stored block, same order, same length, including blocks with no
 * rows. `summary.blocks[i]` describes `template.blocks[i]`. Index it; never
 * key it by `type`, because a Mobility warm-up plus a Mobility cool-down is an
 * ordinary session and the second one would vanish.
 *
 * It counts DEVICES, not sets: in a builder the sets have not happened.
 */
export function useMuscleSummary(templateId: number | null) {
  return useQuery({
    queryKey: muscleSummaryKey(templateId ?? 0),
    queryFn: () =>
      apiFetch<TemplateMuscleSummaryOut>(`/api/templates/${templateId}/muscle-summary`),
    enabled: templateId !== null,
  })
}

/**
 * The equipment at one location, narrowed to what a block of this type can
 * hold. This is the block editor's tray.
 *
 * `modality` is the BLOCK's type, and the filter is deliberate rather than
 * cosmetic: a cardio block must not offer strength machines. One known
 * asymmetry, and it must not be "fixed" by dropping the filter -- brand
 * products are only ever `strength` or `cardio`
 * (`api/routers/locations.py`), so no brand product is ever `mobility` and a
 * Mobility block's tray legitimately offers only standard and custom kit.
 *
 * Keyed on `(locationId, modality)` so switching between a cardio and a
 * strength block does not serve one from the other's cache.
 */
export function useLocationEquipment(
  locationId: number | null,
  modality?: string | null,
) {
  return useQuery({
    queryKey: ['location-equipment', locationId, modality ?? 'all'] as const,
    queryFn: () =>
      apiFetch<LocationEquipmentOut[]>(
        `/api/locations/${locationId}/equipment${modality ? `?modality=${encodeURIComponent(modality)}` : ''}`,
      ),
    enabled: locationId !== null,
  })
}
